import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CircleNotch, FloppyDisk, Warning, CheckCircle, ClipboardText } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MartEntryPage() {
  const { user, token, loading: authLoading } = useAuth();
  const [marts, setMarts] = useState([]);
  const [selectedMart, setSelectedMart] = useState("");
  const [saleDate, setSaleDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [template, setTemplate] = useState(null);
  const [values, setValues] = useState({});
  const [volumes, setVolumes] = useState({});
  const [included, setIncluded] = useState({});
  const [warnings, setWarnings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [recent, setRecent] = useState([]);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token || !user || user.role !== "admin") return;
    axios.get(`${API}/admin/mart-entry/marts`, authHeaders)
      .then((res) => setMarts(res.data.marts))
      .catch(() => toast.error("Could not load marts"));
    fetchRecent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user]);

  const fetchRecent = () => {
    axios.get(`${API}/admin/mart-entry/recent?limit=15`, authHeaders)
      .then((res) => setRecent(res.data.entries))
      .catch(() => {});
  };

  const loadTemplate = useCallback(async (martName) => {
    setSelectedMart(martName);
    setTemplate(null);
    setWarnings([]);
    if (!martName) return;
    setLoadingTemplate(true);
    try {
      const res = await axios.get(`${API}/admin/mart-entry/template?mart_name=${encodeURIComponent(martName)}`, authHeaders);
      setTemplate(res.data);
      const v = {}, inc = {}, vol = {};
      res.data.categories.forEach((c) => {
        v[c.category_id] = c.prefill;
        inc[c.category_id] = true;
        vol[c.category_id] = c.last_volume || "";
      });
      setValues(v);
      setIncluded(inc);
      setVolumes(vol);
    } catch {
      toast.error("Could not load entry template");
    } finally {
      setLoadingTemplate(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const outOfRange = (cat) => {
    const v = parseFloat(values[cat.category_id]);
    if (isNaN(v)) return null;
    if (v < cat.hard_min || v > cat.hard_max) return "hard";
    if (v < cat.plausible_min || v > cat.plausible_max) return "soft";
    return null;
  };

  const save = async (confirmImplausible = false) => {
    const entries = template.categories
      .filter((c) => included[c.category_id] && values[c.category_id] !== "" && !isNaN(parseFloat(values[c.category_id])))
      .map((c) => ({
        category_id: c.category_id,
        price: parseFloat(values[c.category_id]),
        volume: volumes[c.category_id] ? parseInt(volumes[c.category_id]) : null,
      }));
    if (!entries.length) return toast.error("No categories ticked for saving");

    setSaving(true);
    setWarnings([]);
    try {
      const res = await axios.post(`${API}/admin/mart-entry`, {
        mart_name: selectedMart,
        sale_date: saleDate,
        entries,
        confirm_implausible: confirmImplausible,
      }, authHeaders);
      toast.success(`Saved ${res.data.saved} prices for ${selectedMart}`);
      setWarnings([]);
      fetchRecent();
      loadTemplate(selectedMart);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.warnings) {
        setWarnings(detail.warnings);
      } else if (err.response?.status === 422 && detail?.errors) {
        detail.errors.forEach((e) => toast.error(e));
      } else {
        toast.error(typeof detail === "string" ? detail : "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  if (authLoading) return null;
  if (!user || user.role !== "admin") {
    return (
      <div className="min-h-screen bg-[#FFF1E5]">
        <Header />
        <main className="max-w-[600px] mx-auto px-4 py-24 text-center" data-testid="mart-entry-denied">
          <h1 className="font-serif text-3xl text-[#33302E] mb-3">Admin access required</h1>
          <p className="text-[#6B6661]">Sign in with an admin account to enter mart prices.</p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF1E5]" data-testid="mart-entry-page">
      <title>Mart Price Entry | Farmwatch Admin</title>
      <Header />
      <div className="bg-[#022C22] py-8">
        <div className="max-w-[1000px] mx-auto px-4">
          <h1 className="font-serif text-3xl text-white mb-1">Weekly Mart Price Entry</h1>
          <p className="text-[#6EE7B7] text-sm">Real reported prices override the modelled board instantly. Ten minutes a week.</p>
        </div>
      </div>

      <main className="max-w-[1000px] mx-auto px-4 py-8">
        <div className="bg-white border border-[#E0D4C8] p-4 mb-6 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-mono uppercase text-[#6B6661] mb-1">Mart</label>
            <select
              value={selectedMart}
              onChange={(e) => loadTemplate(e.target.value)}
              data-testid="mart-entry-mart-select"
              className="border border-[#D1D5DB] px-3 py-2 text-sm bg-white min-w-[260px]"
            >
              <option value="">Select a mart…</option>
              {marts.map((m) => (
                <option key={m.name} value={m.name}>
                  {m.name} ({m.county}){m.last_entry_date ? ` — last: ${m.last_entry_date}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase text-[#6B6661] mb-1">Sale date</label>
            <input
              type="date"
              value={saleDate}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setSaleDate(e.target.value)}
              data-testid="mart-entry-date"
              className="border border-[#D1D5DB] px-3 py-2 text-sm bg-white"
            />
          </div>
        </div>

        {loadingTemplate && (
          <div className="text-center py-12"><CircleNotch size={32} className="animate-spin text-[#0F766E] mx-auto" /></div>
        )}

        {template && !loadingTemplate && (
          <div className="bg-white border border-[#E0D4C8]" data-testid="mart-entry-grid">
            <div className="px-4 py-3 border-b border-[#E0D4C8] flex items-center gap-2">
              <ClipboardText size={20} className="text-[#0F766E]" />
              <h2 className="font-serif text-lg text-[#33302E]">{template.mart.name} — {template.mart.county}</h2>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E0D4C8] text-left bg-[#F9FAFB]">
                  <th className="px-4 py-2 text-xs font-mono uppercase text-[#6B6661] w-10">Save</th>
                  <th className="px-4 py-2 text-xs font-mono uppercase text-[#6B6661]">Category</th>
                  <th className="px-4 py-2 text-xs font-mono uppercase text-[#6B6661] text-right">Price</th>
                  <th className="px-4 py-2 text-xs font-mono uppercase text-[#6B6661] text-right">Head (opt.)</th>
                  <th className="px-4 py-2 text-xs font-mono uppercase text-[#6B6661]">Typical range</th>
                </tr>
              </thead>
              <tbody>
                {template.categories.map((c) => {
                  const range = outOfRange(c);
                  return (
                    <tr key={c.category_id} className={`border-b border-[#F3F4F6] ${range === "hard" ? "bg-[#FEE2E2]" : range === "soft" ? "bg-[#FEF3C7]" : ""}`}>
                      <td className="px-4 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={included[c.category_id] || false}
                          onChange={(e) => setIncluded({ ...included, [c.category_id]: e.target.checked })}
                          data-testid={`entry-include-${c.category_id}`}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <span className="text-sm text-[#111827]">{c.label}</span>
                        <span className="ml-2 text-[10px] font-mono text-[#9CA3AF]">{c.unit}</span>
                        {c.prefill_source === "last_entry" && (
                          <span className="ml-2 text-[10px] text-[#059669]">pre-filled from {c.last_entry_date}</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <input
                          type="number"
                          step="0.01"
                          value={values[c.category_id]}
                          onChange={(e) => setValues({ ...values, [c.category_id]: e.target.value })}
                          data-testid={`entry-price-${c.category_id}`}
                          className={`border px-2 py-1 text-sm font-mono text-right w-24 ${range ? "border-[#DC2626]" : "border-[#D1D5DB]"}`}
                        />
                      </td>
                      <td className="px-4 py-2 text-right">
                        <input
                          type="number"
                          value={volumes[c.category_id]}
                          onChange={(e) => setVolumes({ ...volumes, [c.category_id]: e.target.value })}
                          data-testid={`entry-volume-${c.category_id}`}
                          className="border border-[#D1D5DB] px-2 py-1 text-sm font-mono text-right w-20"
                        />
                      </td>
                      <td className="px-4 py-2 text-xs font-mono text-[#6B6661]">
                        €{c.plausible_min}–€{c.plausible_max}
                        {range === "soft" && <span className="ml-2 text-[#B45309]"><Warning size={12} className="inline" /> unusual</span>}
                        {range === "hard" && <span className="ml-2 text-[#DC2626]"><Warning size={12} className="inline" /> impossible</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {warnings.length > 0 && (
              <div className="mx-4 mt-4 p-3 bg-[#FEF3C7] border border-[#FCD34D]" data-testid="entry-warnings">
                <p className="text-sm font-semibold text-[#92400E] mb-1">Please double-check before saving:</p>
                {warnings.map((w, i) => <p key={i} className="text-xs text-[#92400E]">• {w}</p>)}
                <button
                  onClick={() => save(true)}
                  disabled={saving}
                  data-testid="entry-confirm-save"
                  className="mt-2 px-4 py-1.5 bg-[#B45309] text-white text-xs font-semibold hover:bg-[#92400E]"
                >
                  The prices are correct — save anyway
                </button>
              </div>
            )}

            <div className="p-4 flex justify-end">
              <button
                onClick={() => save(false)}
                disabled={saving}
                data-testid="entry-save-btn"
                className="px-6 py-2.5 bg-[#0F766E] text-white text-sm font-semibold hover:bg-[#0D6B64] disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />}
                Save prices
              </button>
            </div>
          </div>
        )}

        {recent.length > 0 && (
          <div className="mt-8 bg-white border border-[#E0D4C8]" data-testid="entry-recent">
            <div className="px-4 py-3 border-b border-[#E0D4C8] flex items-center gap-2">
              <CheckCircle size={18} className="text-[#059669]" />
              <h3 className="font-serif text-base text-[#33302E]">Recent entries</h3>
            </div>
            <div className="divide-y divide-[#F3F4F6]">
              {recent.map((e) => (
                <div key={e.id} className="px-4 py-2 flex items-center justify-between text-sm">
                  <span className="text-[#111827]">{e.mart_name} · {e.category_label}</span>
                  <span className="font-mono text-[#4B5563]">€{e.price} <span className="text-[10px] text-[#9CA3AF]">{e.sale_date}</span></span>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
